import { cx } from "shared/utils";
import { Hide, Show } from "shared/components";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import {
  IncomeSourceProvider,
  IncomeType,
  ReverifyRequest,
  VerificationStatus
} from "shared/types";
import { TablesSlice } from "../..";
import { VerifiedReportedStats } from "./VerifiedReportedStats";
import { VerifiedHeader } from "../components/VerifiedHeader";
import { OfferLetterAssets } from "../components/OfferLetterAssets";
import { WarningRecommendation } from "../../components/WarningRecommendation";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import {
  CombinedIncome,
  UploadedIncomeSource,
  VerificationSpecificAdditionaData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { Dispatch, SetStateAction } from "react";
import { UnverifiedIncomeSourceTable } from "./UnverifiedIncomeSourceTable";
import { PayrollDocuments } from "./PayrollDocuments";
import { ReverifyInitiatedBy } from "../../../ReverifySlice/components/ReverifyInitiatedBy";
import { IncomeReverifyTabs } from "../../../ReverifySlice/components/IncomeReverifyTabs";
import { ReverifyProcessingStatus } from "../../../ReverifySlice/components/ReverifyProcessingStatus";
import { ReverifyAttempt } from "../../../ReverifySlice/types";
import { isReverifyTabAPlaceholder } from "../../../ReverifySlice/utils";

interface VerifiedContentProps {
  income: CombinedIncome;
  nonEmptyReverifyAttempt?: VerifiedItem | VerifiedItem[];
  additionalData?: VerificationSpecificAdditionaData | null;
  unverifiedIncomeSources: UploadedIncomeSource[];
  incomeSourceProviders?: Record<number, IncomeSourceProvider | string>;
  incomeSourceTrustScores?: Record<number, number>;
}

const VerifiedContent = ({
  nonEmptyReverifyAttempt,
  additionalData,
  income,
  unverifiedIncomeSources,
  incomeSourceProviders,
  incomeSourceTrustScores
}: VerifiedContentProps) => {
  if (nonEmptyReverifyAttempt)
    return (
      <TablesSlice
        nonEmptyReverifyAttempt={nonEmptyReverifyAttempt}
        incomeStatus={income.status}
        additionalData={additionalData}
        incomeSourceProviders={incomeSourceProviders}
        incomeSourceTrustScores={incomeSourceTrustScores}
      />
    );
  const showUnverifiedIncomeSources =
    ["pending", "finished_by_override"].includes(income.status) &&
    Boolean(unverifiedIncomeSources.length) &&
    // For when an income is reverified after being manually overrode
    income.income_sources_ids?.length === 0;

  if (showUnverifiedIncomeSources)
    return (
      <UnverifiedIncomeSourceTable incomeSources={unverifiedIncomeSources} />
    );
  const hasAssets = "assets" in income;

  return (
    <>
      <p className="text-base text-content-500">No verified income sources</p>
      <div className="h-[1px] w-full bg-alpha-40 my-3" />
      <div className="flex flex-wrap space-x-4">
        {hasAssets &&
          income.assets?.map(({ asset, global_id }) => (
            <AssetItem
              key={global_id}
              url={asset?.url || ""}
              content_type={asset?.content_type || ""}
              filename={asset?.filename || ""}
              assetName={asset?.filename || ""}
            />
          ))}
      </div>
    </>
  );
};

interface VerifiedIncomeDetailsProps {
  reverifyAttempts: ReverifyAttempt[];
  selectedReverifyAttempt?: ReverifyAttempt;
  income: CombinedIncome;
  selectedReverifyAttemptIdx: number;
  setSelectedReverifyAttemptIdx: Dispatch<SetStateAction<number>>;
  setHideProjectedDebtSection: (value: boolean) => void;
  additionalData?: VerificationSpecificAdditionaData | null;
  pendingReverifyRequest?: ReverifyRequest | null;
  applicationId?: string | number;
  verificationId?: number;
  applicantId?: number;
  verificationStatus?: VerificationStatus;
  onReverifyRequestCancelled?: () => void;
  incomeSourceProviders?: Record<number, IncomeSourceProvider | string>;
  incomeSourceTrustScores?: Record<number, number>;
}

export const VerifiedIncomeDetails = ({
  reverifyAttempts,
  selectedReverifyAttempt,
  income,
  selectedReverifyAttemptIdx,
  setSelectedReverifyAttemptIdx,
  setHideProjectedDebtSection,
  additionalData,
  pendingReverifyRequest,
  applicationId,
  verificationId,
  applicantId,
  verificationStatus,
  onReverifyRequestCancelled,
  incomeSourceProviders,
  incomeSourceTrustScores
}: VerifiedIncomeDetailsProps) => {
  const isReverifyPlaceholder = isReverifyTabAPlaceholder(
    selectedReverifyAttempt
  );
  const unverifiedIncomeSources = (income.sources ?? []).filter(
    (source) => source.status !== "reset"
  );

  return (
    <>
      {reverifyAttempts.length > 1 ? (
        <>
          <IncomeReverifyTabs
            selectedReverifyAttemptIdx={selectedReverifyAttemptIdx}
            setSelectedReverifyAttemptIdx={setSelectedReverifyAttemptIdx}
            setHideProjectedDebtSection={setHideProjectedDebtSection}
            reverifyAttempts={reverifyAttempts}
            pendingReverifyRequest={pendingReverifyRequest}
          />
          <OfferLetterAssets income={income} />
        </>
      ) : (
        <ContentTitle
          title={
            income.type === IncomeType.OfferLetter
              ? "Offer letter"
              : "Verification method"
          }
          titleVariant="medium"
        >
          <Show if={income.type === IncomeType.OfferLetter}>
            <WarningRecommendation
              status="Rejected"
              type={income.type}
              rejectionReason={
                "assets" in income && !income.assets?.length
                  ? "No file was uploaded"
                  : ""
              }
              className="mb-4"
            />
          </Show>
          <OfferLetterAssets income={income} />
        </ContentTitle>
      )}

      <ReverifyInitiatedBy selectedReverifyAttempt={selectedReverifyAttempt} />
      <Hide if={income.type === IncomeType.OfferLetter}>
        <div
          key={selectedReverifyAttemptIdx}
          className={cx(
            "pb-4 animate-appear",
            reverifyAttempts.length > 1 ? "pt-4" : ""
          )}
        >
          {isReverifyPlaceholder ? (
            <ReverifyProcessingStatus
              income={income}
              applicationId={applicationId}
              verificationId={verificationId}
              applicantId={applicantId}
              selectedReverifyAttempt={selectedReverifyAttempt}
              verificationStatus={verificationStatus}
              onCancelled={onReverifyRequestCancelled}
            />
          ) : (
            <>
              <VerifiedHeader
                selectedReverifyAttempt={selectedReverifyAttempt}
                income={income}
              />
              <VerifiedReportedStats
                selectedReverifyAttempt={selectedReverifyAttempt}
              />
              <VerifiedContent
                unverifiedIncomeSources={unverifiedIncomeSources}
                nonEmptyReverifyAttempt={selectedReverifyAttempt}
                additionalData={additionalData}
                income={income}
                incomeSourceProviders={incomeSourceProviders}
                incomeSourceTrustScores={incomeSourceTrustScores}
              />
              <PayrollDocuments income={income} />
            </>
          )}
        </div>
      </Hide>
    </>
  );
};
